import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Lottie from "lottie-react";
import axios from "axios";
import { getValidToken } from "../../utils/tokenManager";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import AdminSidebar from "components/common/AdminSidebar";
import MobileClientDashboard from "./mobile/MobileClientDashboard";
import ContactDetailsModal from "components/common/ContactDetailsModal";
import TopWordsCloud from "./adminComponents/TopWordCloud";
import ClientChart from "./adminComponents/ClientChart";
import QualityDemographic from "./adminComponents/QualityDemographic";
import loadingAnim from "../../assets/common/json-Animacin-carga-.json";
import { useTour } from "@reactour/tour";
import { getClientTourSteps } from "./adminTourSteps";

const ClientDashboard = () => {
  const { t } = useTranslation();
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("adminSidebarPinned");
    return saved === "true";
  });

  const clientName = sessionStorage.getItem("clientName") || "";
  const adminTourSteps = React.useMemo(() => getClientTourSteps(t), [t]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const accessToken = await getValidToken();

        const config = {
          method: "get",
          url: `${process.env.REACT_APP_APIBASEURL}/api/dashboard/client-panel`,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        };

        const response = await axios(config);
        setDashboardData(response.data);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const months = React.useMemo(() => {
    if (dashboardData?.months && Array.isArray(dashboardData.months)) {
      return dashboardData?.months?.map((month) => month.label || "");
    }
  }, [dashboardData]);

  const summaryRows = React.useMemo(() => {
    if (!dashboardData?.months) {
      return [
        {
          label: t("dashboard.clientDashboard.Contacts"),
          values: Array(12).fill(0),
        },
        {
          label: t("dashboard.clientDashboard.Calls"),
          values: Array(12).fill(0),
        },
        { label: t("agent.noteInfo.answered"), values: Array(12).fill(0) },
        { label: t("agent.noteInfo.interested"), values: Array(12).fill(0) },
        { label: t("agent.noteInfo.scheduled"), values: Array(12).fill(0) },
        {
          label: t("dashboard.clientDashboard.effectiveScheduled"),
          values: Array(12).fill(0),
        },
        {
          label: t("agent.agentDashboard.cards.outService"),
          values: Array(12).fill(0),
        },
      ];
    }

    const monthData = dashboardData.months;

    return [
      {
        label: t("dashboard.clientDashboard.Contacts"),
        values: monthData?.map((m) => m.total_leads || "-"),
      },
      {
        label: t("dashboard.clientDashboard.Calls"),
        values: monthData?.map((m) => m.total_calls_made || "-"),
      },
      {
        label: t("agent.noteInfo.answered"),
        values: monthData?.map((m) => m.total_calls_answered || "-"),
      },
      {
        label: t("agent.noteInfo.interested"),
        values: monthData?.map((m) => m.total_interested || "-"),
      },
      {
        label: t("agent.noteInfo.scheduled"),
        values: monthData?.map((m) => m.total_meetings_booked || "-"),
      },
      {
        label: t("dashboard.clientDashboard.effectiveScheduled"),
        values: monthData?.map(
          (m) => m.total_meetings_confirmed_attended || "-",
        ),
      },
      {
        label: t("agent.agentDashboard.cards.outService"),
        values: monthData?.map((m) => m.total_contacts_remaining || "-"),
      },
    ];
  }, [dashboardData, t]);

  const scrollContainerRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbLeft, setThumbLeft] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);

  const recalcThumb = () => {
    const sc = scrollContainerRef.current;
    const tr = trackRef.current;
    if (!sc || !tr) return;

    const { scrollWidth, clientWidth, scrollLeft } = sc;
    const trackWidth = tr.clientWidth;

    if (scrollWidth <= clientWidth) {
      setThumbWidth(trackWidth);
      setThumbLeft(0);
      return;
    }

    const newThumbWidth = Math.max(
      (clientWidth / scrollWidth) * trackWidth,
      24,
    );
    setThumbWidth(newThumbWidth);

    const maxThumbLeft = trackWidth - newThumbWidth;
    const newLeft = (scrollLeft / (scrollWidth - clientWidth)) * maxThumbLeft;
    setThumbLeft(newLeft);
  };

  const handleScroll = () => {
    recalcThumb();
  };

  const onThumbMouseDown = (e) => {
    e.preventDefault();
    const sc = scrollContainerRef.current;
    const tr = trackRef.current;
    if (!sc || !tr) return;
    setIsDragging(true);
    dragStartXRef.current = e.clientX;
    dragStartScrollLeftRef.current = sc.scrollLeft;
  };

  useEffect(() => {
    recalcThumb();
    const onResize = () => recalcThumb();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [dashboardData, summaryRows]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging) return;
      const sc = scrollContainerRef.current;
      const tr = trackRef.current;
      if (!sc || !tr) return;

      const trackWidth = tr.clientWidth;
      const maxThumbLeft = Math.max(trackWidth - thumbWidth, 0);
      const { scrollWidth, clientWidth } = sc;

      const deltaX = e.clientX - dragStartXRef.current;
      const scrollRange = Math.max(scrollWidth - clientWidth, 1);
      const thumbRange = Math.max(maxThumbLeft, 1);
      const scrollDelta = (deltaX / thumbRange) * scrollRange;
      sc.scrollLeft = Math.min(
        Math.max(dragStartScrollLeftRef.current + scrollDelta, 0),
        scrollRange,
      );
      recalcThumb();
    };

    const onMouseUp = () => {
      if (isDragging) setIsDragging(false);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDragging, thumbWidth]);

  // Map row labels to backend metric types for details API
  const getMetricType = (rowLabel) => {
    const labelMap = {
      [t("dashboard.clientDashboard.Contacts")]: "leads",
      [t("dashboard.clientDashboard.Calls")]: "calls_made",
      [t("agent.noteInfo.answered")]: "calls_answered",
      [t("agent.noteInfo.interested")]: "interested",
      [t("agent.noteInfo.scheduled")]: "meetings_booked",
      [t("dashboard.clientDashboard.effectiveScheduled")]:
        "meetings_confirmed_attended",
      [t("agent.agentDashboard.cards.outService")]: "contacts_remaining",
    };
    return labelMap[rowLabel] || "leads";
  };

  // Get month date range from API months data, fallback to overall date_range
  const getMonthDateRange = (monthIndex) => {
    const monthData = dashboardData?.months?.[monthIndex];
    if (monthData?.start_date && monthData?.end_date) {
      return {
        start_date: monthData.start_date,
        end_date: monthData.end_date,
      };
    }

    if (
      dashboardData?.date_range?.start_date &&
      dashboardData?.date_range?.end_date
    ) {
      return {
        start_date: dashboardData.date_range.start_date,
        end_date: dashboardData.date_range.end_date,
      };
    }

    // Last resort: compute from index using current year
    const currentYear = new Date().getFullYear();
    const startDate = new Date(currentYear, monthIndex, 1);
    const endDate = new Date(currentYear, monthIndex + 1, 0);
    return {
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
    };
  };

  const handleCellClick = (rowLabel, monthIndex, monthLabel, value) => {
    const metricType = getMetricType(rowLabel);
    const dateRange = getMonthDateRange(monthIndex);

    setSelectedData({
      rowLabel,
      month: monthLabel,
      monthIndex,
      value,
      clientName,
      metricType,
      dateRange,
    });
    setShowContactModal(true);
  };

  const { setIsOpen } = useTour();

  React.useEffect(() => {
    const role = sessionStorage.getItem("userRole");

    const hasSeen = localStorage.getItem("kikonnect_admin_tour_shown");

    if (role !== "Admin" || hasSeen) return;

    let canceled = false;

    const requiredSelectors = adminTourSteps
      .map((step) => step.selector)
      .filter(Boolean);

    const allAnchorsPresent = () =>
      requiredSelectors.every((sel) => {
        try {
          return !!document.querySelector(sel);
        } catch {
          return false;
        }
      });

    const start = Date.now();

    const timeoutMs = 2000;

    const tryOpen = () => {
      if (canceled) return;

      if (allAnchorsPresent() || Date.now() - start > timeoutMs) {
        setIsOpen(true);

        localStorage.setItem("kikonnect_admin_tour_shown", "1");

        return;
      }

      requestAnimationFrame(tryOpen);
    };

    const raf = requestAnimationFrame(tryOpen);

    return () => {
      canceled = true;

      cancelAnimationFrame(raf);
    };
  }, [setIsOpen, adminTourSteps]);

  return (
    <>
    <div className="hidden lg:flex min-h-screen bg-secondaryBg">
      <AdminSidebar
        logoIcon={logoIcon}
        logout={logout}
        labelClassName="bg-primary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div
        className={`flex-1 flex flex-col px-4 sm:px-8 py-6 md:py-7 transition-all duration-300 ${
          sidebarPinned ? "ml-[250px]" : "ml-[80px]"
        }`}
      >
        <div className="max-w-6xl mx-auto w-full">
          <h1 className="text-[30px] font-bold text-primary mt-10 mb-2">
            {t("dashboard.dashboard")} {clientName}
          </h1>
          <div className="relative flex items-center mb-8">
            <span className="absolute left-0 w-1 h-1 bg-primary rounded-full -ml-1"></span>
            <hr className="flex-1 border-t-2 border-primary" />
            <span className="absolute right-0 w-1 h-1 bg-primary rounded-full -mr-1"></span>
          </div>
          {loading && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
              <div className="bg-white rounded-lg text-secondary text-xl shadow-xl p-6 relative flex flex-col items-center">
                <Lottie
                  animationData={loadingAnim}
                  loop
                  autoplay
                  style={{ height: "5rem", width: "5rem" }}
                />
              </div>
            </div>
          )}
          {error ? (
            <div className="bg-white rounded-[20px] shadow-lg p-8 w-[360px] md:w-full border border-[#E3E8F0] text-center">
              <p className="text-red-500">
                {t("error.generic") || "Error loading dashboard data"}
              </p>
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="bg-white rounded-[20px] shadow-lg p-0 w-[270px] md:w-auto h-auto border border-[#E3E8F0] overflow-x-auto scrollbar-none"
            >
              <table className="w-full text-primary">
                <thead>
                  <tr className="text-white">
                    <th className="bg-primary py-3 px-3 text-left w-[220px]">
                      &nbsp;
                    </th>

                    {(months?.length ? months : Array(12).fill("")).map(
                      (m, index) => (
                        <th
                          key={index}
                          className="bg-primary py-3 px-3 text-center w-[115px]"
                        >
                          {m || ""}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((row) => (
                    <tr key={row.label} className="border-b last:border-b-0">
                      <td className="py-3 px-3 text-left text-primary font-semibold text-sm">
                        {row.label}
                      </td>
                      {row.values.map((v, i) => (
                        <td
                          key={`${row.label}-${i}`}
                          className="py-3 px-3 text-center font-light text-primary cursor-pointer hover:text-secondary hover:underline transition-colors"
                          onClick={() =>
                            handleCellClick(row.label, i, months[i], v)
                          }
                          data-tour-id="clients-table"
                        >
                          {v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div
            ref={trackRef}
            className="h-[6px] bg-buttonFalse mt-5 mb-10 rounded-full relative select-none"
          >
            <div
              ref={thumbRef}
              onMouseDown={onThumbMouseDown}
              style={{ width: `${thumbWidth}px`, left: `${thumbLeft}px` }}
              className="h-[6px] bg-primary rounded-full absolute top-0 cursor-grab active:cursor-grabbing"
            ></div>
          </div>

          <QualityDemographic dashboardData={dashboardData} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
            <TopWordsCloud dashboardData={dashboardData} />

            <ClientChart dashboardData={dashboardData} />
          </div>
        </div>
      </div>

      <ContactDetailsModal
        open={showContactModal}
        onClose={() => setShowContactModal(false)}
        data={selectedData}
      />
    </div>
    <div className="lg:hidden">
      <MobileClientDashboard />
    </div>
    </>
  );
};

export default ClientDashboard;
